import {
  ApiError,
  CertificateStatus,
  DiagnosticFailure,
  DiagnosticStage,
  ErrorCode,
  EsocialEnvironment,
  StageOutcome,
  type EsocialStageResponse,
} from '../../api';

/**
 * The words the eSocial connection screen uses, and the failures it has to
 * explain.
 *
 * A lookup module like [`certificates`](./certificates.ts) and
 * [`mailbox`](./mailbox.ts), and here for a reason those two only half share.
 * The backend writes one sentence per stage, in English, for whoever is
 * integrating against the API. The person reading this screen is a vyttech
 * analyst at nine in the morning with a filing that stopped going through, and
 * what they need is pt-BR and an instruction.
 *
 * So the screen keys its own wording on `failure` — the stable code the API
 * documents and promises to keep — rather than translating `summary`. The
 * English sentence is still shown underneath, because it carries the detail
 * ours cannot: which endpoint, which date, which CNPJ.
 *
 * The one thing that is **not** translated is eSocial's own `descResposta`. It
 * is already pt-BR, it is the government's wording rather than ours, and it is
 * what an operator will quote when they open a ticket with the Receita Federal.
 */

export const ESOCIAL_COPY = {
  title: 'Conexão eSocial',
  description:
    'Verifica, em quatro etapas, se a plataforma consegue falar com o eSocial: se há ' +
    'certificado instalado e válido, se a conexão segura é aceita, se — quando uma empresa é ' +
    'escolhida — o eSocial responde a uma consulta pelo CNPJ dela e se o portal web do eSocial ' +
    'abre num navegador que apresenta o certificado.',

  runTitle: 'Testar a conexão',
  runDescription:
    'O teste pode ser feito sem escolher empresa: as duas primeiras etapas não dependem de ' +
    'nenhum cliente, e são exatamente as que interessam a quem está configurando o ambiente.',
  company: 'Empresa (opcional)',
  companyHint:
    'Escolha uma empresa para incluir a terceira etapa: uma consulta real ao eSocial pelo CNPJ ' +
    'dela. Sem empresa, o teste para depois da conexão.',
  companyNone: 'Nenhuma — testar só certificado e conexão',
  companiesLoading: 'Carregando empresas…',
  browserUrl: 'Página para o navegador (opcional)',
  browserUrlHint:
    'Só para este teste: o navegador abre esta página em vez da página de login do eSocial. ' +
    'Precisa começar com https://. O certificado só é apresentado às origens configuradas em ' +
    'I8LIFE_BROWSER_CERTIFICATE_ORIGINS; qualquer outro endereço é visitado sem ele.',
  run: 'Testar conexão',
  running: 'Testando…',
  runFailed: 'Não foi possível executar o teste.',

  resultTitle: 'Resultado',
  resultPassed: 'Conexão com o eSocial funcionando.',
  resultFailed: 'A conexão com o eSocial não está funcionando.',
  resultCaption: 'Etapas do teste de conexão com o eSocial',
  empty: 'Nenhum teste executado ainda',
  emptyDescription: 'Use o botão acima. Cada execução fica registrada na auditoria.',

  stage: 'Etapa',
  outcome: 'Situação',
  detail: 'Detalhe',
  environment: 'Ambiente',
  endpoint: 'Endereço',
  employer: 'CNPJ consultado',
  tlsVersion: 'Protocolo',
  esocialResponse: 'Resposta do eSocial',
  eventsFound: 'Eventos encontrados na janela',
  browserBackend: 'Navegador do portal',

  screenshotTitle: 'O que o navegador viu',
  screenshotAlt: 'Captura de tela da página aberta pelo navegador',
  screenshotFinalUrl: 'Endereço final',
  screenshotStatus: 'Status HTTP',
  screenshotPageTitle: 'Título da página',

  certificateTitle: 'Certificado usado no teste',
  certificateDescription:
    'O certificado A1 mais recente que ainda pode ser usado. A senha e a chave privada nunca ' +
    'saem do servidor: nenhuma tela e nenhum endpoint devolve qualquer uma das duas.',
  certificateHolder: 'Titular',
  certificateCnpj: 'CNPJ',
  certificateValidUntil: 'Válido até',
  certificateFingerprint: 'Impressão digital (SHA-256)',
  certificateStatus: 'Situação',
  certificateNone: 'Nenhum certificado instalado.',

  /** What the server says, shown under our sentence rather than instead of it. */
  serverDetail: 'Detalhe do servidor',
} as const;

/** The four checks, in the order they run. */
export const STAGE_LABELS: Record<DiagnosticStage, string> = {
  [DiagnosticStage.CERTIFICATE]: 'Certificado digital',
  [DiagnosticStage.HANDSHAKE]: 'Conexão segura (mTLS)',
  [DiagnosticStage.QUERY]: 'Consulta ao eSocial',
  [DiagnosticStage.BROWSER]: 'Portal eSocial (navegador)',
};

/** What each check proves, said once so the table does not have to. */
export const STAGE_DESCRIPTIONS: Record<DiagnosticStage, string> = {
  [DiagnosticStage.CERTIFICATE]:
    'Há um certificado A1 instalado, ele abre com a senha guardada e está dentro da validade.',
  [DiagnosticStage.HANDSHAKE]:
    'O eSocial aceita o certificado e a conexão segura se estabelece. Não depende de empresa.',
  [DiagnosticStage.QUERY]:
    'O eSocial responde a uma consulta de eventos pelo CNPJ da empresa escolhida.',
  [DiagnosticStage.BROWSER]:
    'Um navegador no servidor abre o portal do eSocial apresentando o certificado. É um canal ' +
    'separado do serviço web: não depende da conexão segura nem da consulta.',
};

export const OUTCOME_LABELS: Record<StageOutcome, string> = {
  [StageOutcome.PASSED]: 'Passou',
  [StageOutcome.FAILED]: 'Falhou',
  [StageOutcome.SKIPPED]: 'Não executada',
};

/**
 * The tone each outcome is shown in.
 *
 * `skipped` is neutral and deliberately not a warning. A stage that did not run
 * because the one before it failed is a consequence, not a second problem, and
 * painting it red would show three alarms where there is one.
 */
export const OUTCOME_TONES: Record<StageOutcome, 'ok' | 'warning' | 'blocking'> = {
  [StageOutcome.PASSED]: 'ok',
  [StageOutcome.FAILED]: 'blocking',
  [StageOutcome.SKIPPED]: 'warning',
};

export const ENVIRONMENT_LABELS: Record<EsocialEnvironment, string> = {
  [EsocialEnvironment.PRODUCAO_RESTRITA]: 'Produção restrita',
  [EsocialEnvironment.PRODUCAO]: 'Produção',
};

export const CERTIFICATE_STATUS_LABELS: Record<CertificateStatus, string> = {
  [CertificateStatus.ACTIVE]: 'Válido',
  [CertificateStatus.EXPIRING_SOON]: 'Vence em breve',
  [CertificateStatus.EXPIRED]: 'Vencido',
  [CertificateStatus.SUPERSEDED]: 'Substituído',
  [CertificateStatus.DEACTIVATED]: 'Desativado',
};

/**
 * What went wrong and what to do about it, in pt-BR, keyed on the API's own
 * `failure` code.
 *
 * Seven codes for the web service, seven genuinely different actions for the
 * person at the screen — install a certificate, buy a new one, reinstall it,
 * fix an address, talk to the Receita Federal, try again, grant a procuração —
 * and three for the browser, which is a channel of its own. The generic sentence an
 * HTTP status maps to says none of them, and the seven cannot be told apart by
 * status at all: the whole run is a `200`.
 */
export const FAILURE_MESSAGES: Record<DiagnosticFailure, string> = {
  [DiagnosticFailure.NO_CERTIFICATE]:
    'Nenhum certificado A1 está instalado. Instale o arquivo .pfx ou .p12 emitido pela ' +
    'autoridade certificadora na tela Certificado digital.',
  [DiagnosticFailure.CERTIFICATE_UNUSABLE]:
    'Todos os certificados instalados estão fora de uso — vencidos, substituídos ou ' +
    'desativados. Providencie a renovação junto à autoridade certificadora e instale o novo.',
  [DiagnosticFailure.CERTIFICATE_UNREADABLE]:
    'O certificado instalado não abriu com a senha guardada ao lado dele. Instale-o novamente ' +
    'com a senha emitida pela autoridade certificadora.',
  [DiagnosticFailure.ENDPOINT_UNREACHABLE]:
    'O endereço do eSocial não respondeu. Confira o ambiente configurado e se este servidor ' +
    'tem acesso de rede ao eSocial.',
  [DiagnosticFailure.HANDSHAKE_REFUSED]:
    'O eSocial recusou o certificado apresentado. Confira se é o certificado que a Receita ' +
    'Federal conhece para este CNPJ, se não foi revogado e se vale para o ambiente configurado.',
  [DiagnosticFailure.QUERY_UNANSWERED]:
    'A conexão foi estabelecida, mas a consulta não foi respondida. O problema não é do ' +
    'certificado: tente de novo e, se persistir, confira a situação dos serviços do eSocial.',
  [DiagnosticFailure.QUERY_REFUSED]:
    'O eSocial respondeu recusando a consulta. Confira se o CNPJ é mesmo o do empregador e se ' +
    'a plataforma tem procuração eletrônica para consultá-lo. O código e a mensagem do ' +
    'eSocial estão ao lado.',
  [DiagnosticFailure.BROWSER_UNAVAILABLE]:
    'O navegador não pôde ser iniciado neste servidor. Confira se o Chromium está instalado — ' +
    'a imagem do backend já o traz; em desenvolvimento, rode make browser-install.',
  [DiagnosticFailure.BROWSER_CERTIFICATE_UNUSABLE]:
    'O certificado abriu para o serviço web, mas não pôde ser preparado para o navegador. ' +
    'Instale-o novamente a partir do arquivo emitido pela autoridade certificadora.',
  [DiagnosticFailure.BROWSER_NAVIGATION_FAILED]:
    'O navegador não conseguiu abrir a página: falha de rede, de TLS ou tempo esgotado. ' +
    'Confira se este servidor alcança o endereço por HTTPS e se ele está correto; a mensagem ' +
    'do navegador está logo abaixo.',
  [DiagnosticFailure.BROWSER_CAPTCHA_CHALLENGED]:
    'O gov.br pediu um desafio de captcha visível depois de "Seu certificado digital", e a ' +
    'verificação automática não consegue respondê-lo. O gov.br está desconfiando deste ' +
    'navegador ou do endereço deste servidor: entre pelo portal do eSocial manualmente desta ' +
    'vez, ou rode a verificação de novo mais tarde.',
  [DiagnosticFailure.BROWSER_TWO_STEP_REQUIRED]:
    'O gov.br pediu a verificação em duas etapas: um código gerado no aplicativo gov.br, que ' +
    'só uma pessoa pode digitar. Use o login assistido — o navegador remoto, com alguém diante ' +
    'dele —, como descreve o roteiro em docs/deploy/staging.md.',
  [DiagnosticFailure.BROWSER_TWO_STEP_UNANSWERED]:
    'O gov.br pediu a verificação em duas etapas no navegador remoto, e ninguém digitou o ' +
    'código do aplicativo gov.br a tempo. Rode de novo com a pessoa que tem o aplicativo ' +
    'gov.br por perto e alguém diante do navegador remoto, ou com um tempo limite maior.',
};

/**
 * The sentence to show for one stage: ours when the API named a failure, the
 * stage's own description when it passed, and the server's words when a stage
 * was skipped (which is where the server explains *why*).
 */
export function stageMessage(stage: EsocialStageResponse): string {
  if (stage.failure !== null && stage.failure !== undefined) {
    return FAILURE_MESSAGES[stage.failure];
  }
  if (stage.outcome === StageOutcome.SKIPPED) {
    if (stage.stage === DiagnosticStage.BROWSER) {
      return (
        'O navegador não foi usado: este ambiente não tem navegador configurado ' +
        '(I8LIFE_BROWSER_BACKEND) ou o certificado não passou.'
      );
    }
    return stage.stage === DiagnosticStage.QUERY
      ? 'Nenhuma empresa foi escolhida, ou uma etapa anterior falhou.'
      : 'Uma etapa anterior falhou, então esta não foi executada.';
  }
  return STAGE_DESCRIPTIONS[stage.stage];
}

/**
 * Why the *request* failed, as opposed to why a stage did.
 *
 * Three refusals happen before any stage runs, and each is a different thing to
 * go and do: the client is not turned on in this deployment, the company has no
 * CNPJ recorded, or the company is gone. The generic sentence each error code
 * maps to says none of them, and `ApiError.message` is deliberately generic
 * because it has to serve every screen.
 *
 * Returns `undefined` to let the shared wording stand, which is the right answer
 * for anything this screen has nothing specific to add about.
 */
export function requestFailureMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  if (error.code === ErrorCode.SERVICE_UNAVAILABLE) {
    return (
      'Este ambiente não tem cliente eSocial configurado, então não há conexão a testar. ' +
      'Falar com o governo é algo que cada ambiente liga de propósito: configure ' +
      'I8LIFE_ESOCIAL_BACKEND e I8LIFE_ESOCIAL_ENVIRONMENT.'
    );
  }
  if (error.details.some((detail) => detail.field === 'company_id')) {
    return (
      'A empresa escolhida não tem CNPJ cadastrado. O eSocial identifica o empregador pelo CNPJ ' +
      'e por mais nada, então não há consulta a fazer: cadastre o CNPJ da empresa, ou rode o ' +
      'teste sem escolher empresa para verificar só o certificado e a conexão.'
    );
  }
  if (error.isNotFound) {
    return 'A empresa escolhida não existe mais. Atualize a página e escolha outra.';
  }
  return undefined;
}
